import React, { useEffect, useState } from 'react';
import { staffingAPI } from '@/services/staffingAPI';
import { Users, UserPlus, Copy, CheckCircle2, Loader2 } from 'lucide-react';
import Pagination, { usePagination } from '@/components/shared/Pagination';
import ScrollHint from '@/components/shared/ScrollHint';

export default function StaffingTeam() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [lastInvite, setLastInvite] = useState(null);
  const [copied, setCopied] = useState(false);
  const pg = usePagination(team, 20);

  const load = async () => {
    setLoading(true);
    try { setTeam((await staffingAPI.team()).team || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const r = await staffingAPI.invite(inviteEmail, inviteName);
      setLastInvite(r);
      setInviteEmail(''); setInviteName('');
      await load();
    } catch (err) { alert(err?.response?.data?.detail || 'Invite failed'); }
    setInviting(false);
  };

  const revoke = async (uid) => {
    if (!window.confirm('Revoke this member?')) return;
    try { await staffingAPI.revokeSub(uid); await load(); }
    catch (err) { alert(err?.response?.data?.detail || 'Failed'); }
  };

  const restore = async (uid) => {
    try { await staffingAPI.restoreSub(uid); await load(); }
    catch (err) { alert(err?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="staffing-team">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2"><Users className="w-5 h-5" /> Team</h1>

      <form onSubmit={invite} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl p-5 mb-6">
        <p className="text-slate-900 dark:text-white text-sm font-medium mb-1 flex items-center gap-2"><UserPlus className="w-4 h-4" /> Invite a subordinate</p>
        <p className="text-slate-500 dark:text-zinc-500 text-xs mb-3">They&apos;ll receive temporary credentials. Email must use your org domain.</p>
        <div className="flex gap-2 flex-wrap">
          <input value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="Name (optional)" className="flex-1 min-w-[140px] bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white" data-testid="invite-name-input" />
          <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} type="email" required placeholder="email@your-org.com" className="flex-1 min-w-[200px] bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white" data-testid="invite-email-input" />
          <button type="submit" disabled={inviting} className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black rounded-full text-sm font-medium disabled:opacity-50" data-testid="invite-submit-btn">{inviting ? 'Inviting…' : 'Invite'}</button>
        </div>
      </form>

      {lastInvite && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 mb-6" data-testid="invite-result">
          <p className="text-emerald-700 dark:text-emerald-300 text-sm font-medium mb-2">Invite created — share these credentials securely:</p>
          <div className="text-xs text-slate-600 dark:text-zinc-300 space-y-1 font-mono">
            <div>Email: <span className="text-slate-900 dark:text-white">{lastInvite.subordinate.email}</span></div>
            <div className="flex items-center gap-2">
              Password: <span className="text-slate-900 dark:text-white">{lastInvite.temporary_password}</span>
              <button onClick={() => { navigator.clipboard.writeText(lastInvite.temporary_password); setCopied(true); setTimeout(() => setCopied(false), 1200); }} className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300">
                {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        </div>
      )}

      <ScrollHint>
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/60 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-white dark:bg-[#0e0e10] text-slate-500 dark:text-zinc-500 text-xs uppercase tracking-wider">
            <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Position</th><th className="text-left p-3">Status</th><th className="text-right p-3">Actions</th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-6 text-center text-slate-500 dark:text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></td></tr>}
            {!loading && pg.pageItems.map(m => (
              <tr key={m.user_id} className="border-t border-slate-200 dark:border-zinc-800/50">
                <td className="p-3 text-white">{m.name || '—'} {m.is_head && <span className="ml-2 px-1.5 py-0.5 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 rounded text-[10px]">HEAD</span>}</td>
                <td className="p-3 text-slate-500 dark:text-zinc-400 text-xs">{m.email}</td>
                <td className="p-3 text-slate-500 dark:text-zinc-400 text-xs">{m.position}</td>
                <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded ${m.member_status === 'active' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-red-500/15 text-red-700 dark:text-red-300'}`}>{m.member_status}</span></td>
                <td className="p-3 text-right">
                  {!m.is_head && (
                    m.member_status === 'revoked'
                      ? <button onClick={() => restore(m.user_id)} className="text-emerald-600 dark:text-emerald-400 text-xs hover:underline">Restore</button>
                      : <button onClick={() => revoke(m.user_id)} className="text-red-600 dark:text-red-400 text-xs hover:underline" data-testid={`revoke-${m.user_id}`}>Revoke</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </ScrollHint>
      <Pagination {...pg.paginationProps} testIdPrefix="staffing-team-pg" />
    </div>
  );
}
